import { createSlice } from "@reduxjs/toolkit";
import Swal from "sweetalert2";
import { errorHandler } from "../../../helpers/errorHandler";
import { Call } from "../../../helpers/fetch";
import {
    startUILoading,
    stopUILoading,
} from "../../actions/administrador/ui/ui";

const initialState = {
    active: null,
    list: [],
    pagination: {
        data: [],
        per_page: 10,
        total: 0,
    },
    report: null,
    reportByStudentNumber: null,
    openModal: false,
};

export const EarlyInterventions = createSlice({
    name: "EarlyInterventions",
    initialState,
    reducers: {
        setActive: (state, action) => {
            state.active = action.payload;
        },
        unsetActive: (state) => {
            state.active = null;
        },
        setActiveEarlyInterventionStatus: (state, action) => {
            if (state.active?.early_intervention?.status) {
                state.active.early_intervention.status = action.payload;
            }
        },
        setList: (state, action) => {
            state.list = action.payload;
        },
        clearList: (state) => {
            state.list = initialState.list;
        },
        setPagination: (state, action) => {
            state.pagination = action.payload;
        },
        clearPagination: (state) => {
            state.pagination = initialState.pagination;
        },
        setReport: (state, action) => {
            state.report = action.payload;
        },
        clearReport: (state) => {
            state.report = initialState.report;
        },
        setReportByStudentNumber: (state, action) => {
            state.reportByStudentNumber = action.payload;
        },
        clearReportByStudentNumber: (state) => {
            state.reportByStudentNumber = initialState.reportByStudentNumber;
        },
        setOpenModal: (state, action) => {
            state.openModal = action.payload;
        },
    },
});

export const {
    setActive,
    unsetActive,
    setActiveEarlyInterventionStatus,
    setList,
    clearList,
    setPagination,
    clearPagination,
    setReport,
    clearReport,
    setReportByStudentNumber,
    clearReportByStudentNumber,
    setOpenModal,
} = EarlyInterventions.actions;

export default EarlyInterventions.reducer;

export const startShowEarlyInterventions = ({
    page = 1,
    query = "",
    number_rows = 25,
    asList = 0,
    status = null,
    category = null,
    cycle_id = null,
    academic_record_id = null,
}) => {
    return async (dispatch) => {
        if (!asList) dispatch(startUILoading());

        const params = new URLSearchParams();

        params.set("page", page);
        params.set("q", query);
        params.set("per_page", number_rows);
        params.set("list", asList);

        if (status) params.set("status", status);
        if (category) params.set("category", category);
        if (cycle_id) params.set("cycle_id", cycle_id);
        if (academic_record_id)
            params.set("academic_record_id", academic_record_id);

        const resp = await Call(
            "operators/students/early-interventions",
            "GET",
            params.toString()
        );

        if (resp.success) {
            if (asList) {
                dispatch(setList(resp.success));
            } else {
                dispatch(setPagination(resp.success));
            }
        } else {
            errorHandler(resp);
        }

        if (!asList) dispatch(stopUILoading());
    };
};

export const startCreateEarlyIntervention = (
    values,
    setErrors = () => {},
    callback = () => {},
    dispatchLoading = true
) => {
    return async (dispatch) => {
        if (dispatchLoading) {
            dispatch(startUILoading());
        }

        const resp = await Call(
            "operators/students/early-interventions",
            "POST",
            values
        );

        if (resp.success) {
            Swal.fire({
                title: "Éxito",
                text: "Intervención registrada.",
                icon: "success",
                toast: true,
                position: "bottom-end",
                timer: 8000,
            });

            await callback(resp.success);
        } else {
            errorHandler(resp, setErrors);
        }

        if (dispatchLoading) {
            dispatch(stopUILoading());
        }
    };
};

export const startAddEarlyInterventionFollowup = (
    earlyInterventionId,
    values,
    setErrors = () => {},
    callback = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(
            `operators/students/early-interventions/${earlyInterventionId}/followups`,
            "POST",
            values
        );

        if (resp.success) {
            Swal.fire({
                title: "Éxito",
                text: "Seguimiento agregado.",
                icon: "success",
                toast: true,
                position: "bottom-end",
                timer: 8000,
            });
            callback(resp.success);
        } else {
            errorHandler(resp, setErrors);
        }

        dispatch(stopUILoading());
    };
};

export const startGetEarlyInterventionReport = (
    { cycle, curriculum_id, student_number = null } = {},
    setErrors = () => {},
    callback = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const params = new URLSearchParams();

        if (cycle) {
            params.set("cycle", cycle);
        }
        if (curriculum_id) {
            params.set("curriculum_id", curriculum_id);
        }
        if (student_number) {
            params.set("student_number", student_number);
        }

        const resp = await Call(
            "operators/students/early-interventions/report",
            "GET",
            params.toString()
        );

        if (resp.success) {
            if (student_number) {
                dispatch(setReportByStudentNumber(resp.success));
            } else {
                dispatch(setReport(resp.success));
            }

            callback(resp.success);
        } else {
            errorHandler(resp, setErrors);
        }

        dispatch(stopUILoading());
    };
};

export const startUpdateEarlyInterventionStatus = (
    early_intervention_id,
    values,
    setErrors = () => {},
    callback = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(
            `operators/students/early-interventions/update-status/${early_intervention_id}`,
            "PUT",
            values
        );

        if (resp.success) {
            await callback();
            await dispatch(
                setActiveEarlyInterventionStatus(resp.success.status)
            );

            Swal.fire({
                title: "Éxito",
                text: "Estatus de intervención actualizado.",
                icon: "success",
                toast: true,
                position: "bottom-end",
                timer: 8000,
            });
        } else {
            errorHandler(resp, setErrors);
        }

        dispatch(stopUILoading());
    };
};
